/**
 * Onboarding state machine — tracks the "Watch This" sequence beats.
 * Vanilla TS with listener pattern (no framework dependency).
 *
 * Completion is stored in:
 *   - D1 users.onboarding_complete (via DataManager, persistent)
 *   - localStorage (fallback / quick check before DataManager loads)
 */

import type { OnboardingBeat } from '@/types';
import { dataManager } from '@/data/data-manager';

type OnboardingListener = (beat: OnboardingBeat, prevBeat: OnboardingBeat) => void;

const STORAGE_KEY = 'hatchlings-onboarding-complete';

export class OnboardingState {
  private beat: OnboardingBeat = 'IDLE';
  private listeners: Set<OnboardingListener> = new Set();
  private completed: boolean;

  constructor() {
    // Check DataManager first (D1), fall back to localStorage
    if (dataManager.initialized) {
      this.completed = dataManager.isOnboardingComplete;
    } else {
      this.completed = localStorage.getItem(STORAGE_KEY) === 'true';
    }
  }

  get currentBeat(): OnboardingBeat { return this.beat; }
  get isComplete(): boolean { return this.completed; }
  get isActive(): boolean { return this.beat !== 'IDLE' && this.beat !== 'COMPLETE'; }

  transition(newBeat: OnboardingBeat): void {
    const prev = this.beat;
    this.beat = newBeat;
    if (newBeat === 'COMPLETE') {
      this.completed = true;
      // Save to localStorage (immediate, for quick check on next load)
      localStorage.setItem(STORAGE_KEY, 'true');
      // Save to D1 (persistent, survives browser clear)
      dataManager.setOnboardingComplete().catch((err) => {
        console.warn('Failed to save onboarding state to D1:', err);
      });
    }
    for (const fn of this.listeners) {
      fn(newBeat, prev);
    }
  }

  onChange(listener: OnboardingListener): () => void {
    this.listeners.add(listener);
    return () => { this.listeners.delete(listener); };
  }

  reset(): void {
    this.completed = false;
    localStorage.removeItem(STORAGE_KEY);
    this.beat = 'IDLE';
  }
}
